import React from 'react'

import { usePreloadImage } from '@sketch/utils'
import {
  PublicationThumbnailFragment,
  ShareThumbnailsFragment,
} from '@sketch/gql-types'

import { getPublicationThumbnail } from '../../utils'

import {
  Thumbnail,
  NoImageIcon,
  NoImageThumbnail,
  Skeleton,
  ShareThumbnail,
  ShareThumbnailWrapper,
  BlurredBackground,
} from './PublicationThumbnail.styles'
import { thumbnailForPreviewFile } from '../../../shares/utils'

interface PublicationExistingThumbnailProps {
  className?: string
  name: string
  srcSet: string
  fallback?: string
  isShareThumbnail?: boolean
}

const PublicationExistingThumbnail = (
  props: PublicationExistingThumbnailProps
) => {
  const { className, srcSet, name, fallback, isShareThumbnail } = props

  const { isLoaded, ref } = usePreloadImage({
    srcSet,
  })

  if (isLoaded) {
    return <Skeleton className={className} ref={ref} width="100%" />
  }

  if (isShareThumbnail) {
    return (
      <ShareThumbnailWrapper>
        <ShareThumbnail
          className={className}
          alt={name}
          src={fallback}
          srcSet={srcSet}
        />
        <BlurredBackground src={srcSet ?? fallback} srcSet={srcSet} />
      </ShareThumbnailWrapper>
    )
  }

  return (
    <Thumbnail
      className={className}
      alt={name}
      src={fallback}
      srcSet={srcSet}
    />
  )
}

export interface PublicationThumbnailProps {
  className?: string
  previewFile: PublicationThumbnailFragment['previewFile'] | null
  sharePreviewFile?: ShareThumbnailsFragment | null
  name: string
}

const PublicationThumbnail = (props: PublicationThumbnailProps) => {
  const { className, previewFile, name, sharePreviewFile } = props
  const thumbnail = getPublicationThumbnail(previewFile)

  const srcSet = thumbnail
    ? `${thumbnail.standard} 1x, ${thumbnail.retina} 2x`
    : undefined

  if (srcSet) {
    return (
      <PublicationExistingThumbnail
        className={className}
        name={name}
        srcSet={srcSet}
        fallback={thumbnail?.standard}
      />
    )
  }

  const sharePreviewFileUrl = thumbnailForPreviewFile(sharePreviewFile)

  if (sharePreviewFileUrl) {
    return (
      <PublicationExistingThumbnail
        className={className}
        name={name}
        srcSet={sharePreviewFileUrl}
        fallback={thumbnail?.standard}
        isShareThumbnail
      />
    )
  }

  return (
    <NoImageThumbnail className={className}>
      <NoImageIcon aria-label="No Image available" />
    </NoImageThumbnail>
  )
}

export default PublicationThumbnail
